import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Add, Close, Delete, Inventory2, Remove, Save } from '@mui/icons-material'
import { apiService } from '../../../services/api'
import type { PalworldItem, KitInicial } from '../../../types/palworldItems'

export type PalworldKitInicialLine = {
  id: string
  item: PalworldItem
  quantity: number
}

const clampQty = (q: number) => Math.max(1, Math.trunc(Number(q) || 1))

const PalworldKitInicialDrawer: React.FC<{
  open: boolean
  onClose: () => void
  lines: PalworldKitInicialLine[]
  setLineQuantity: (id: string, quantity: number) => void
  removeLine: (id: string) => void
  clearCart: () => void
  kitInicial?: KitInicial | null
}> = ({ open, onClose, lines, setLineQuantity, removeLine, clearCart, kitInicial }) => {
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [successOpen, setSuccessOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string>('')

  const totalItems = useMemo(() => lines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0), [lines])

  const isEditing = useMemo(() => !!kitInicial?.id, [kitInicial?.id])

  const canSave = useMemo(() => {
    if (saving) return false
    if (lines.length === 0) return false
    if (!nome.trim()) return false
    if (!descricao.trim()) return false
    return true
  }, [descricao, lines.length, nome, saving])

  useEffect(() => {
    if (kitInicial) {
      setNome(kitInicial.nome || '')
      setDescricao(kitInicial.descricao || '')
    } else {
      setNome('')
      setDescricao('')
    }
  }, [kitInicial])

  const doSave = async () => {
    if (!canSave) return
    setSaveError(null)
    setSaving(true)
    try {
      const payload = {
        nome: nome.trim(),
        descricao: descricao.trim(),
        itens: lines.map((l) => ({ itemId: l.item.id, quantidade: clampQty(l.quantity) }))
      }

      if (isEditing && kitInicial?.id) {
        await apiService.atualizarKitInicial(kitInicial.id, payload)
        const msg = `Kit inicial "${nome.trim()}" atualizado com sucesso! O kit contém ${totalItems} itens.`
        setSuccessMessage(msg)
      } else {
        await apiService.criarKitInicial(payload)
        const msg = `Kit inicial "${nome.trim()}" criado com sucesso! O kit contém ${totalItems} itens.`
        setSuccessMessage(msg)
      }

      clearCart()
      setNome('')
      setDescricao('')
      setSuccessOpen(true)
      onClose()
    } catch (e: any) {
      setSaveError(e?.message || 'Erro ao salvar kit inicial.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => (saving ? null : onClose())}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 540 },
            backgroundColor: 'rgba(12, 13, 24, 0.98)',
            borderLeft: '1px solid rgba(76, 175, 80, 0.22)'
          }
        }}
        transitionDuration={250}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.08)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Inventory2 sx={{ color: '#4caf50' }} />
              <Typography variant="h6" fontWeight={900}>
                {isEditing ? 'Editar Kit Inicial' : 'Criar Kit Inicial'}
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'text.secondary' }} disabled={saving}>
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
            <Stack spacing={2}>
              <Alert severity="info">
                Crie kits iniciais para novos jogadores. Selecione itens na lista e adicione ao kit.
              </Alert>

              <TextField
                label="Nome do Kit"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                fullWidth
                placeholder="Ex: Kit Inicial Básico"
              />

              <TextField
                label="Descrição do Kit"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                required
                fullWidth
                multiline
                minRows={3}
                placeholder="Ex: Kit inicial para novos jogadores com itens essenciais"
              />

              <Divider sx={{ opacity: 0.25 }} />

              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  Itens do Kit
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total: {totalItems}
                </Typography>
              </Box>

              {lines.length === 0 ? (
                <Box sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(255,255,255,0.03)' }}>
                  <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Nenhum item selecionado</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Volte na lista e clique em "+1" para adicionar itens ao kit.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {lines.map((l) => (
                    <Box
                      key={l.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        p: 1.25,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.10)',
                        bgcolor: 'rgba(255,255,255,0.03)'
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={l.item.imageUrl}
                        alt={l.item.name ?? l.item.assetName ?? 'item'}
                        sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.08)' }}
                        imgProps={{ loading: 'lazy' }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                          {l.item.name ?? '—'}
                        </Typography>
                        {!!l.item.assetName && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {l.item.assetName}
                          </Typography>
                        )}
                      </Box>

                      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => setLineQuantity(l.id, clampQty(l.quantity - 1))}
                          sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                          aria-label="Diminuir quantidade"
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        <TextField
                          size="small"
                          value={clampQty(l.quantity)}
                          onChange={(e) => setLineQuantity(l.id, clampQty(Number(e.target.value)))}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, style: { textAlign: 'center', width: 72 } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setLineQuantity(l.id, clampQty(l.quantity + 1))}
                          sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
                          aria-label="Aumentar quantidade"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Stack>

                      <IconButton
                        onClick={() => removeLine(l.id)}
                        sx={{ color: 'text.secondary' }}
                        aria-label="Remover item"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}

              {saveError && <Alert severity="error">{saveError}</Alert>}
            </Stack>
          </Box>

          <Box sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: 'rgba(0,0,0,0.18)' }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={clearCart}
                  disabled={saving || lines.length === 0}
                  sx={{ color: 'text.secondary', flex: 1 }}
                >
                  Limpar kit
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={() => void doSave()}
                  disabled={!canSave}
                  sx={{ flex: 2, fontWeight: 900, py: 1.2, background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)', color: '#1a3a1a' }}
                >
                  {saving ? 'Salvando…' : (isEditing ? 'Atualizar Kit' : 'Criar Kit')}
                </Button>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                O kit inicial será salvo no sistema e poderá ser atribuído a novos jogadores.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.86)' } }}
        PaperProps={{
          sx: {
            bgcolor: '#0c0e1c',
            backgroundImage: 'none',
            border: '1px solid rgba(255,255,255,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>{isEditing ? 'Kit atualizado com sucesso' : 'Kit criado com sucesso'}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: 'text.secondary' }}>{successMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="contained" onClick={() => setSuccessOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PalworldKitInicialDrawer
